import {
  Button,
  ButtonGroup,
  Card,
  Input,
  Label,
  TextAreaField,
} from "@aws-amplify/ui-react"
import { EuDataActDataSharingSchema } from "@strtmn/sds/schemas"
import {
  type AddressObjectDto,
  type EuDataActDataSharingDto,
  type MachineSupaDto,
  type OrganizationDto,
  type UserDataDto,
} from "@strtmn/sds/types"
import { dateOnly, getErrorMessage } from "@strtmn/strtmn"
import { I18n } from "aws-amplify/utils"
import { useContext, useEffect, useState, type PropsWithChildren } from "react"
import {
  FormProvider,
  get,
  useForm,
  type FieldErrors,
  type SubmitHandler,
} from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { requestDataSharing } from "../../api/EuDataActApi"
import { CancelButton, ErrorBanner, LoadingErrorState } from "../../components"
import VehiclePageLayout from "../../components/VehiclePageLayout"
import { isProd } from "../../config"
import { UserDataContext, UserDataContextHelper } from "../../contexts"
import { useActiveOrgId } from "../../hooks"
import { useYupValidationResolver } from "../../hooks/useYupValidationResolver"
import { getLinkToVehicleEuDataAct } from "../../LinkUtils"
import { getCurrentUserId } from "../../Utils.AmplifyAuth"

interface PageWrapperProps extends PropsWithChildren {
  vin: string
}
function PageWrapper({ vin, children }: PageWrapperProps) {
  return (
    <VehiclePageLayout vin={vin}>
      <Card>
        <div className="section-heading mb-6">
          {I18n.get("Weitergabe von Daten an Dritte nach Art. 5 Data Act")}
        </div>
        {children}
      </Card>
    </VehiclePageLayout>
  )
}

/** @todo: extract */
interface IResponseMessage {
  type: "success" | "load-error" | "submit-error"
  message: string
}

export default function DataSharing() {
  const navigate = useNavigate()
  const userPrefLang = "de" // useUserPrefLang()
  const params = useParams<{
    vin: string | undefined
  }>()
  const { userData } = useContext(UserDataContext)
  const activeOrgId = useActiveOrgId()

  const [currentUserId, setCurrentUserId] = useState<string | undefined>(
    undefined
  )
  const [loading, setLoading] = useState(true)
  const [responseMessage, setResponseMessage] =
    useState<IResponseMessage | null>(null)
  const [isFormSubmitting, setIsFormSubmitting] = useState(false)
  const now = new Date()

  const vin = params.vin!
  let machineFromUserCtx = undefined
  if (activeOrgId && userData) {
    const org = UserDataContextHelper.findOrg(activeOrgId, userData)
    const machinesFromUserCtx = org.Machines
    !isProd &&
      console.debug(
        `${machinesFromUserCtx.length} machines from userData:`,
        machinesFromUserCtx
      )
    machineFromUserCtx = machinesFromUserCtx.find(
      (supa) => supa.serial_number === vin
    )
  }

  const loadedUserData: UserDataDto = userData!
  const allAddresses: { orgName: string; address: AddressObjectDto }[] = []
  loadedUserData.Organizations.forEach((o: OrganizationDto) => {
    const a: AddressObjectDto =
      typeof o.address === "string"
        ? (JSON.parse(o.address) as AddressObjectDto)
        : o.address
    allAddresses.push({ orgName: o.name, address: a })
  })
  const hasExactlyOneAddress = allAddresses.length === 1

  const userAddressValue = allAddresses
    .flatMap(({ orgName, address }) => {
      const lines: string[] = []
      if (orgName) {
        lines.push(orgName)
      }
      if (address) {
        // If street is there, add a line. Number may be empty.
        if (address.street) {
          lines.push(`${address.street} ${address.number}`.trim())
        }
        if (address.country || address.zip || address.city) {
          lines.push(`${address.country} ${address.zip} ${address.city}`.trim())
        }
      }
      if (lines.length > 0) {
        return lines.join("\n")
      }
      return []
    })
    .join("\n\n")

  const userNameList: string[] = []
  if (hasExactlyOneAddress && allAddresses[0]?.orgName) {
    userNameList.push(allAddresses[0]?.orgName.trim())
    userNameList.push("/")
  }
  loadedUserData.first_name &&
    userNameList.push(loadedUserData.first_name.trim())
  loadedUserData.last_name && userNameList.push(loadedUserData.last_name.trim())
  const userName = userNameList.join(" ")

  const resolver = useYupValidationResolver(EuDataActDataSharingSchema)
  const methods = useForm<EuDataActDataSharingDto>({
    // values: {
    //   userId: currentUserId
    // },
    defaultValues: {
      requestingName: userName,
    },
    resolver,
  })
  const {
    formState: { errors },
    handleSubmit,
    register,
    setError,
    setValue,
    watch,
  } = methods

  useEffect(() => {
    console.debug("> data-sharing.useEffect (load data)")

    const fetchAllData = async () => {
      !isProd && console.debug("> data-sharing.fetchAllData")
      setLoading(true)
      setCurrentUserId(undefined)
      setResponseMessage(null)
      try {
        const myCurrentUserId = await getCurrentUserId()
        setCurrentUserId(myCurrentUserId)
        setValue("userId", myCurrentUserId)
      } catch (err) {
        console.error("Error:", err)
        setResponseMessage({
          type: "load-error",
          message: getErrorMessage(err),
        })
      } finally {
        setLoading(false)
      }
    }

    fetchAllData()
  }, [])

  if (loading || responseMessage?.type === "load-error") {
    return (
      <PageWrapper vin={vin}>
        <LoadingErrorState
          isLoading={loading}
          loadingMessage={I18n.get("Lade Maschinendaten, bitte warten...")}
          isError={!!(responseMessage?.type === "load-error")}
          errorMessage={`${I18n.get("Fehler")}: ${I18n.get(responseMessage?.message!)}`}
        />
      </PageWrapper>
    )
  }
  if (!machineFromUserCtx) {
    return (
      <PageWrapper vin={vin}>
        <ErrorBanner
          error={`${I18n.get("Fehler")}: ${I18n.get("Keine Maschinendaten geladen.")}`}
        />
      </PageWrapper>
    )
  }

  const loadedMachine: MachineSupaDto = machineFromUserCtx
  !isProd && console.log("loadedMachine =", loadedMachine)
  const activeTerms = loadedMachine.EuDataActTerms?.filter(
    (t) => t.cancel_date === null
  )
  let dateOfTerms: string
  if (!activeTerms || activeTerms.length === 0) {
    // If the form would only be for machines with active terms,
    // then this would be an error in the process / backend.
    console.warn("⚡⚡⚡ Machine has no active EU Data Act terms.")
    dateOfTerms = "[GOT NO TERMS]"
  } else {
    dateOfTerms = dateOnly(new Date().toISOString())
    for (const terms of activeTerms) {
      const tAcceptDate = dateOnly(terms.accept_date)
      if (tAcceptDate < dateOfTerms) {
        dateOfTerms = tAcceptDate
      }
    }
  }

  const onSubmit: SubmitHandler<any> = async (
    data: EuDataActDataSharingDto
  ) => {
    !isProd && console.debug("> onSubmit")
    setIsFormSubmitting(true)
    !isProd && console.log("onSubmit: data =", data)
    setResponseMessage(null)
    try {
      await requestDataSharing(data)
      navigate(
        getLinkToVehicleEuDataAct({ vin: loadedMachine.serial_number }),
        {
          replace: true,
          state: {
            successMessage: I18n.get(
              "Die Anfrage zur Weitergabe von Daten wurde versendet."
            ),
          },
        }
      )
    } catch (err) {
      console.error("Could not submit the form:", getErrorMessage(err))
      setResponseMessage({
        type: "submit-error",
        message: getErrorMessage(err),
      })
    } finally {
      setIsFormSubmitting(false)
    }
  }

  const onError = (_errors: FieldErrors) => {
    !isProd && console.log("Validation failed: onError", _errors)
    setError("root.clientError", {
      type: "validate",
      message: I18n.get(
        "Das Formular enthält ungültige Eingaben. Beachten Sie die Fehlermeldungen."
      ),
    })
  }

  const requestingName = watch("requestingName")

  return (
    <PageWrapper vin={vin}>
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit, onError)}
          className="max-w-screen-md"
        >
          <div className="text-center">{I18n.get("zwischen")}</div>
          <div>
            1. {requestingName}
            {/* {(requestingName ? `${requestingName} / ` : "") +
              loadedMachine.adresse.bezeichnung} */}
            {/* [{I18n.get("Nutzer des vernetzten Produkts")}] */}
          </div>
          <div className="text-right">
            - {I18n.get('nachfolgend auch "Nutzer" genannt')} -
          </div>
          <div className="text-center">{I18n.get("und")}</div>
          <div>2. B. Strautmann & Söhne GmbH u. Co. KG</div>
          <div className="text-right">
            - {I18n.get('nachfolgend auch "Dateninhaber" genannt')} -
          </div>
          <div>&nbsp;</div>
          <div>
            {I18n.get(
              "Hiermit verlange ich als Nutzer oder als eine im Namen des Nutzers handelnde Partei, dass der Dateninhaber dem unten genannten Dritten ohne Weiteres verfügbare Daten sowie die für die Auslegung und Nutzung dieser Daten erforderlichen Metadaten unverzüglich, für den Nutzer unentgeltlich, in derselben Qualität, die dem Dateninhaber zur Verfügung steht, einfach, sicher, in einem umfassenden, strukturierten, gängigen und maschinenlesbaren Format und, soweit relevant und technisch durchführbar, kontinuierlich und in Echtzeit bereitstellt."
            )}
          </div>
          <div>&nbsp;</div>
          <div>{I18n.get("Angaben zum Dritten")}</div>
          <div>
            <Label>
              {I18n.get("Name des Dritten")}
              <Input
                {...register("thirdPartysName")}
                size="small"
                placeholder={I18n.get("Name des Dritten")}
              />
            </Label>
            {errors.thirdPartysName && (
              <span className="error">
                {I18n.get(errors.thirdPartysName.message!)}
              </span>
            )}
          </div>
          <div>
            <Label>
              {I18n.get("Anschrift des Dritten")}
              <TextAreaField
                {...register("thirdPartysAddress")}
                size="small"
                label={I18n.get("Anschrift des Dritten")}
                labelHidden={true}
              ></TextAreaField>
            </Label>
            {errors.thirdPartysAddress && (
              <span className="error">
                {I18n.get(errors.thirdPartysAddress.message!)}
              </span>
            )}
          </div>
          <div>
            <Label>
              {I18n.get("E-Mail-Adresse des Dritten")}
              <Input
                {...register("thirdPartysEmail")}
                size="small"
                placeholder={I18n.get("E-Mail-Adresse des Dritten")}
              />
            </Label>
            {errors.thirdPartysEmail && (
              <span className="error">
                {I18n.get(errors.thirdPartysEmail.message!)}
              </span>
            )}
          </div>
          <div>
            <Label>
              {I18n.get("Telefonnummer des Dritten")}
              <Input
                {...register("thirdPartysPhone")}
                size="small"
                placeholder={I18n.get("Telefonnummer des Dritten")}
              />
            </Label>
            {errors.thirdPartysPhone && (
              <span className="error">
                {I18n.get(errors.thirdPartysPhone.message!)}
              </span>
            )}
          </div>
          <div>&nbsp;</div>
          <div>
            {I18n.get(
              "Art. 5 Absatz 1 Satz 2, Absatz 2 bis 13 Data Act gilt entsprechend."
            )}
          </div>
          <div>&nbsp;</div>
          <div>
            <Label>{I18n.get("Maschinennummer")}</Label>
            <br />
            {vin}
            <input
              type="hidden"
              {...register("machineSerialNumber")}
              value={vin}
            />
          </div>
          <div>&nbsp;</div>
          <div>
            <Label>
              {I18n.get(
                "Ihr Name als Nutzer oder als eine im Namen des Nutzers handelnde Partei"
              )}
              <Input
                {...register("requestingName")}
                size="small"
                placeholder={I18n.get(
                  "Ihr Name als Nutzer oder als eine im Namen des Nutzers handelnde Partei"
                )}
                defaultValue={userName}
              />
            </Label>
            {get(errors, "requestingName") && (
              <span className="error">
                {I18n.get(get(errors, "requestingName").message)}
              </span>
            )}
          </div>
          <div>
            <Label>
              {I18n.get("Name und Anschrift des Nutzers")}
              <TextAreaField
                {...register("userNameAndAddress")}
                size="small"
                resize="vertical"
                rows={5}
                label={I18n.get("Name und Anschrift des Nutzers")}
                labelHidden={true}
                defaultValue={userAddressValue}
              />
            </Label>
            {get(errors, "userNameAndAddress") && (
              <span className="error">
                {I18n.get(get(errors, "userNameAndAddress").message)}
              </span>
            )}
          </div>
          <div>&nbsp;</div>
          <div>
            <Label>{I18n.get("Datum")}</Label>
            <br />
            {now.toLocaleDateString(userPrefLang, { dateStyle: "long" })}
            <input
              type="hidden"
              {...register("dateOfRequest")}
              value={dateOnly(now.toISOString())}
            />
            {get(errors, "dateOfRequest") && (
              <span className="error">
                {I18n.get(get(errors, "dateOfRequest").message)}
              </span>
            )}
          </div>

          <input type="hidden" {...register("userId")} value={currentUserId} />
          {get(errors, "userId") && (
            <span className="error">
              {I18n.get(get(errors, "userId").message)}
            </span>
          )}

          {get(errors, "root.clientError") && (
            <ErrorBanner
              error={I18n.get(get(errors, "root.clientError").message)}
            />
          )}

          {responseMessage?.type === "submit-error" && (
            <ErrorBanner error={I18n.get(responseMessage.message)} />
          )}

          <ButtonGroup className="buttons !mt-4" size="small">
            <Button
              type="submit"
              variation="primary"
              isDisabled={isFormSubmitting}
              isLoading={isFormSubmitting}
              loadingText={I18n.get("Anfrage wird gesendet...")}
            >
              {I18n.get("Anfrage senden")}
            </Button>

            <CancelButton
              href={getLinkToVehicleEuDataAct({
                vin: loadedMachine.serial_number,
              })}
              title="Abbrechen"
              isDisabled={isFormSubmitting}
            />
          </ButtonGroup>
        </form>
      </FormProvider>
    </PageWrapper>
  )
}
